"use client";

import { useState, useEffect } from "react";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import TandMList from "./TimeAndMaterialList";
import TandMForm from "./TimeAndMaterialForm";
import TandMView from "./TimeAndMaterialView";
import AlertDialog from "./AlertDialog";

export default function TimeAndMaterialTab({ project, setProject }) {
  const navigate = useNavigate();
  const [timeAndMaterials, setTimeAndMaterials] = useState([]);
  const [activeTab, setActiveTab] = useState("Pending");
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [preparedBy, setPreparedBy] = useState(null);
  const [formData, setFormData] = useState({
    labourItems: [],
    materialItems: [],
    scopeOfWork: "",
    signature: null,
    signedBy: "",
    overheadPercentage: 0,
  });
  const [availableLabourItems, setAvailableLabourItems] = useState([]);
  const [availableMaterialItems, setAvailableMaterialItems] = useState([]);
  const [selectedLabourItem, setSelectedLabourItem] = useState(null);
  const [selectedMaterialItem, setSelectedMaterialItem] = useState(null);
  const [labourQty, setLabourQty] = useState("");
  const [materialQty, setMaterialQty] = useState("");
  const [images, setImages] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedLabourBidArea, setSelectedLabourBidArea] = useState(null);
  const [
    selectedMaterialAnticipatedBidArea,
    setSelectedMaterialAnticipatedBidArea,
  ] = useState(null);
  const [selectedLabourCostCode, setSelectedLabourCostCode] = useState(null);
  const [selectedMaterialCostCode, setSelectedMaterialCostCode] =
    useState(null);
  const [bidAreaOptions, setBidAreaOptions] = useState([]);
  const [alert, setAlert] = useState({
    open: false,
    type: "error",
    title: "",
    message: "",
  });
  const [editingLabourItemIndex, setEditingLabourItemIndex] = useState(null);
  const [editingMaterialItemIndex, setEditingMaterialItemIndex] =
    useState(null);
  const [editLabourForm, setEditLabourForm] = useState({
    contractLabourItem: null,
    quantityUsed: "",
    bidArea: null,
    costCode: null,
    customer: true,
    budget: true,
  });
  const [editMaterialForm, setEditMaterialForm] = useState({
    budgetLineItem: null,
    quantityUsed: "",
    vendor: null,
    bidArea: null,
    costCode: null,
    customer: true,
    budget: true,
  });
  const [rejectConfirm, setRejectConfirm] = useState({
    open: false,
    entryId: null,
  });

  const handleDeleteLabourItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      labourItems: prev.labourItems.filter((_, idx) => idx !== index),
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!project?._id) {
        console.warn("Project ID is missing, skipping fetchData");
        setAvailableLabourItems([]);
        setAvailableMaterialItems([]);
        setBidAreaOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        console.log("Project data:", project); // Debug project object

        const [
          tmResponse,
          vendorsResponse,
          costCodesResponse,
          companyProfileResponse,
        ] = await Promise.all([
          axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/accounts/vendors",
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/costCodes",
            { headers: { Authorization: `Bearer ${token}` } }
          ),
          axios.get(
            "https://erp-production-72da01c8e651.herokuapp.com/api/company-profile",
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);
        // Validate tmResponse.data
        const validatedData = Array.isArray(tmResponse.data)
          ? tmResponse.data.map((entry) => ({
              ...entry,
              materialItems: Array.isArray(entry.materialItems)
                ? entry.materialItems.map((item) => ({
                    ...item,
                    totalCost: item.totalCost ?? 0,
                    totalPrice: item.totalPrice ?? 0,
                    vendor: item.vendor?._id || item.vendor || null,
                    bidArea: item.bidArea || "",
                    costCode: item.costCode?._id || item.costCode || null,
                    customer: item.customer ?? true,
                    budget: item.budget ?? true,
                  }))
                : [],
              labourItems: Array.isArray(entry.labourItems)
                ? entry.labourItems.map((item) => ({
                    ...item,
                    bidArea: item.bidArea || "",
                    costCode: item.costCode?._id || item.costCode || null,
                    customer: item.customer ?? true,
                    budget: item.budget ?? true,
                  }))
                : [],
              overheadPercentage: entry.overheadPercentage || 0,
            }))
          : [];

        setTimeAndMaterials(validatedData);
        setCostCodes(costCodesResponse.data || []);
        setCompanyProfile(companyProfileResponse.data || {});

        // Combine contract labour items and budget labour items
        const seenLabels = new Set();
        const labourOptions = [];

        // Add contract labour items
        if (Array.isArray(project?.contractLabourItems)) {
          project.contractLabourItems.forEach((item) => {
            const label = `${item.labourItem?.title || "Unnamed"} ($${
              item.unitRate || 0
            } / ${item.uom || "N/A"})`;
            if (!seenLabels.has(label)) {
              seenLabels.add(label);
              labourOptions.push({
                value: item._id,
                label,
                unitRate: item.unitRate || 0,
                unitCost: item.unitCost || 0,
                uom: item.uom || "N/A",
                costCode: item.costCode?._id || item.costCode || null,
                isContractItem: true,
              });
            }
          });
        } else {
          console.warn(
            "project.contractLabourItems is not an array or is undefined:",
            project?.contractLabourItems
          );
        }

        // Add labour items from budgets
        if (Array.isArray(project?.budgets)) {
          project.budgets.forEach((budget) => {
            if (Array.isArray(budget?.lineItems)) {
              budget.lineItems
                .filter((item) => item.category === "L")
                .forEach((item) => {
                  const label = `${item.costCodeDescription || "Unnamed"} ($${
                    item.unitRate || 0
                  } / ${item.uom || "N/A"})`;
                  if (!seenLabels.has(label)) {
                    seenLabels.add(label);
                    labourOptions.push({
                      value: item._id,
                      label,
                      unitRate: item.unitRate || 0,
                      unitCost: item.cost || item.unitRate || 0,
                      uom: item.uom || "N/A",
                      costCode: item.costCode?._id || item.costCode || null,
                      bidArea: item.bidArea || "",
                      isContractItem: false,
                    });
                  }
                });
            } else {
              console.warn(
                "budget.lineItems is not an array or is undefined for budget:",
                budget
              );
            }
          });
        } else {
          console.warn(
            "project.budgets is not an array or is undefined:",
            project?.budgets
          );
        }

        setAvailableLabourItems(labourOptions);

        // Material items
        const seenMaterialLabels = new Set();
        const materialOptions = [];
        if (Array.isArray(project?.budgets)) {
          project.budgets.forEach((budget) => {
            if (Array.isArray(budget?.lineItems)) {
              budget.lineItems
                .filter((item) => item.category === "M")
                .forEach((item) => {
                  const label = `${item.itemDescription || "Unnamed"} ($${
                    item.cost || 0
                  } / ${item.container || "N/A"}) - ${item.size || "N/A"}`;
                  if (!seenMaterialLabels.has(label)) {
                    seenMaterialLabels.add(label);
                    materialOptions.push({
                      value: item._id,
                      label,
                      size: item.size || "N/A",
                      cost: item.cost || 0,
                      budgetLineItem: item._id,
                      price: item.price || 0,
                      container: item.container || "N/A",
                      itemDescription: item.itemDescription || "Unnamed",
                      vendor: item.vendor?._id || item.vendor || null,
                      bidArea: item.bidArea || "",
                      costCode: item.costCode?._id || item.costCode || null,
                      itemCode: item.itemCode || "N/A",
                    });
                  }
                });
            }
          });
        }

        setAvailableMaterialItems(materialOptions);

        // Bid areas
        const bidAreas = [];
        if (Array.isArray(project?.budgets) && project.budgets.length > 0) {
          const firstBudget = project.budgets[0];
          if (Array.isArray(firstBudget?.lineItems)) {
            const uniqueBidAreas = [
              ...new Set(
                firstBudget.lineItems
                  .filter(
                    (item) => item.bidArea && typeof item.bidArea === "string"
                  )
                  .map((item) => item.bidArea)
              ),
            ];
            bidAreas.push(
              ...uniqueBidAreas.map((bidArea) => ({
                value: bidArea,
                label: bidArea,
              }))
            );
          }
        }
        setBidAreaOptions(bidAreas);

        const vendorData = Array.isArray(vendorsResponse.data)
          ? vendorsResponse.data.filter((v) => v.type === "vendor")
          : [];
        setVendors(vendorData);

        setLoading(false);
      } catch (err) {
        setAlert({
          open: true,
          type: "error",
          title: "Error",
          message: "Failed to load data.",
        });
        console.error("Error in fetchData:", err);
        setLoading(false);
      }
    };

    fetchData();
  }, [project]);

  const vendorOptions = vendors.map((vendor) => ({
    value: vendor._id,
    label: vendor.companyName,
  }));

  const costCodeOptions = costCodes.map((costCode) => ({
    value: costCode._id,
    label: `${costCode.code} - ${costCode.description}`,
  }));

  const handleCreateToggle = () => {
    setIsCreating(!isCreating);
    setIsEditing(false);
    setEditingId(null);
    setViewMode("list");
    setSelectedEntry(null);
    setFormData({
      labourItems: [],
      materialItems: [],
      scopeOfWork: "",
      signature: null,
      signedBy: "",
      overheadPercentage: companyProfile?.defaultOverhead || 0,
    });
    setSelectedLabourItem(null);
    setSelectedMaterialItem(null);
    setLabourQty("");
    setMaterialQty("");
    setSelectedVendor(null);
    setSelectedLabourBidArea(null);
    setSelectedMaterialAnticipatedBidArea(null);
    setSelectedLabourCostCode(null);
    setSelectedMaterialCostCode(null);
    setImages([]);
    setPreparedBy(null);
    setEditingLabourItemIndex(null);
    setEditingMaterialItemIndex(null);
    setAlert({ open: false, type: "error", title: "", message: "" });
  };

  const handleEdit = (entry) => {
    if (entry.status !== "Pending" && entry.status !== "Rejected") {
      setAlert({
        open: true,
        type: "error",
        title: "Cannot Edit",
        message: "Can only edit Pending or Rejected entries.",
      });
      return;
    }
    setIsEditing(true);
    setIsCreating(true);
    setEditingId(entry._id);
    setViewMode("edit");
    try {
      setFormData({
        labourItems: entry.labourItems.map((item) => ({
          contractLabourItem:
            typeof item.contractLabourItem === "object" &&
            item.contractLabourItem?._id
              ? item.contractLabourItem._id
              : item.contractLabourItem || null,
          budgetLineItem:
            typeof item.budgetLineItem === "object" && item.budgetLineItem?._id
              ? item.budgetLineItem._id
              : item.budgetLineItem || null,
          quantityUsed: item.quantityUsed || 0,
          totalPrice: item.totalPrice || 0,
          totalCost: item.totalCost || 0,
          bidArea: item.bidArea || "",
          costCode: item.costCode?._id || item.costCode || null,
          customer: item.customer ?? true,
          budget: item.budget ?? true,
          unitRate: item.unitRate || 0,
          uom: item.uom || "N/A",
        })),
        materialItems: entry.materialItems.map((item) => {
          const materialOption = availableMaterialItems.find(
            (opt) =>
              opt.value ===
              (typeof item.budgetLineItem === "object"
                ? item.budgetLineItem?._id
                : item.budgetLineItem)
          );
          return {
            budgetLineItem:
              typeof item.budgetLineItem === "object" &&
              item.budgetLineItem?._id
                ? item.budgetLineItem._id
                : item.budgetLineItem || null,
            quantityUsed: item.quantityUsed || 0,
            totalCost: item.totalCost || 0,
            totalPrice: item.totalPrice || 0,
            price: item.price || 0,
            cost: item.cost || 0,
            size: item.size || materialOption?.size || "N/A",
            itemCode: item.itemCode || materialOption?.itemCode || "N/A",
            itemDescription:
              item.itemDescription ||
              materialOption?.itemDescription ||
              "Unnamed",
            container: item.container || materialOption?.container || "N/A",
            vendor: item.vendor?._id || item.vendor || null,
            bidArea: item.bidArea || "",
            costCode: item.costCode?._id || item.costCode || null,
            customer: item.customer ?? true,
            budget: item.budget ?? true,
          };
        }),
        scopeOfWork: entry.scopeOfWork || "",
        signature: entry.signature || null,
        signedBy: entry.signedBy || "",
        overheadPercentage: entry.overheadPercentage || 0,
      });
      setPreparedBy(entry.createdBy?.displayName || "");
      setImages(entry.images || []);
      setAlert({ open: false, type: "error", title: "", message: "" });
    } catch (err) {
      console.error("Error in handleEdit:", err);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to load entry for editing.",
      });
    }
  };

  const handleView = (entry) => {
    setSelectedEntry(entry);
    setViewMode("view");
  };

  const generateTandMPDF = async (formDataOverride = null) => {
    if (!companyProfile) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Cannot generate PDF: Company profile is not set.",
      });
      return;
    }

    const entry = formDataOverride || selectedEntry;

    console.log(entry);

    if (!entry) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "No entry or form data available to generate PDF.",
      });
      return;
    }

    // Validate entry structure
    if (!Array.isArray(entry.labourItems)) {
      console.warn("entry.labourItems is not an array:", entry.labourItems);
      entry.labourItems = [];
    }
    if (!Array.isArray(entry.materialItems)) {
      console.warn("entry.materialItems is not an array:", entry.materialItems);
      entry.materialItems = [];
    }
    if (!Array.isArray(entry.surchargeItems)) {
      console.warn(
        "entry.surchargeItems is not an array:",
        entry.surchargeItems
      );
      entry.surchargeItems = [];
    }

    try {
      const response = await fetch("/t_and_m_template.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64))
          throw new Error("Invalid base64 data");
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: (img, tagValue, tagName) => {
          if (tagName === "companyLogo") return [150, 100];
          if (tagName === "signature") return [100, 150];
          if (tagName === "image") return [200, 200];
          return [150, 100];
        },
      };
      const imageModule = new ImageModule(imageOptions);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [imageModule],
      });

      let companyLogoBase64 = null;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        try {
          const logoResponse = await fetch(companyProfile.logo, {
            mode: "cors",
          });
          if (!logoResponse.ok)
            throw new Error(`Failed to fetch logo: ${logoResponse.statusText}`);
          const logoBlob = await logoResponse.blob();
          companyLogoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        } catch (err) {
          console.error("Error fetching company logo:", err.message);
          companyLogoBase64 = null;
        }
      } else if (companyProfile?.logo) {
        companyLogoBase64 = companyProfile.logo;
      }

      let signatureBase64 = null;
      if (entry.signature) {
        try {
          const sigResponse = await fetch(
            entry.signature instanceof File
              ? URL.createObjectURL(entry.signature)
              : entry.signature,
            { mode: "cors" }
          );
          if (!sigResponse.ok)
            throw new Error(
              `Failed to fetch signature: ${sigResponse.statusText}`
            );
          const sigBlob = await sigResponse.blob();
          signatureBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(sigBlob);
          });
        } catch (err) {
          console.warn("Failed to fetch signature:", err.message);
          signatureBase64 = null;
        }
      }

      const imagesPromises = (entry.images || []).map(async (url, index) => {
        let imageBase64 = null;
        try {
          const imgResponse = await fetch(
            url instanceof File ? URL.createObjectURL(url) : url,
            { mode: "cors" }
          );
          if (!imgResponse.ok)
            throw new Error(`Failed to fetch image: ${imgResponse.statusText}`);
          const imgBlob = await imgResponse.blob();
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(imgBlob);
          });
        } catch (err) {
          console.warn(`Failed to fetch image ${index + 1}: ${err.message}`);
          imageBase64 = null;
        }
        return { image: imageBase64 };
      });

      const resolvedImages = await Promise.all(imagesPromises);

      // Process labour items
      const labourItems = entry.labourItems
        .filter((item) => item && item.customer === true)
        .map((item, index) => {
          const labourOption = availableLabourItems.find(
            (opt) =>
              opt.value ===
              (item.contractLabourItem?._id ||
                item.contractLabourItem ||
                item.budgetLineItem?._id ||
                item.budgetLineItem)
          );
          const description = labourOption
            ? labourOption.label.split(" (")[0]
            : item.contractLabourItem?.labourItem?.title ||
              item.budgetLineItem?.costCodeDescription ||
              "Unnamed Labour Item";
          const unitPrice =
            item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
          return {
            itemNo: index + 1,
            description,
            quantity: item.quantityUsed || 0,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: (item.totalPrice || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      // Process material items
      const materialItems = entry.materialItems
        .filter((item) => item && item.customer === true)
        .map((item, index) => {
          const materialOption = availableMaterialItems.find(
            (opt) =>
              opt.value === (item.budgetLineItem?._id || item.budgetLineItem)
          );
          const description = `${
            item.itemDescription || materialOption?.itemDescription || "Unnamed"
          } (${item.container || materialOption?.container || "N/A"} - ${
            item.size || materialOption?.size || "N/A"
          })`;
          const unitPrice =
            item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
          return {
            itemNo: labourItems.length + index + 1,
            description,
            quantity: item.quantityUsed || 0,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: (item.totalPrice || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      // Process surcharge items (if any)
      const surchargeItems = entry.surchargeItems
        .filter((item) => item && item.customer === true)
        .map((item, index) => {
          const unitPrice =
            item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
          return {
            itemNo: labourItems.length + materialItems.length + index + 1,
            description: item.title || "Unnamed Surcharge",
            quantity: item.quantityUsed || 0,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: (item.totalPrice || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      const items = [...labourItems, ...materialItems, ...surchargeItems];

      // Log items for debugging
      console.log("Generated items for PDF:", items);

      if (items.length === 0) {
        console.warn(
          "No items to include in PDF. Check labourItems, materialItems, and surchargeItems."
        );
      }

      // Calculate financials
      const subtotalPrice = items.reduce((sum, item) => {
        const amount = parseFloat(item.amount.replace(/[^0-9.-]+/g, "")) || 0;
        return sum + amount;
      }, 0);
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const taxAmount = subtotalPrice * (taxRate / 100);
      const totalPrice = subtotalPrice + taxAmount;

      const data = {
        projectName: project.projectName || "Unknown Project",
        date: formDataOverride
          ? new Date().toLocaleDateString()
          : new Date(entry.signedAt || Date.now()).toLocaleDateString(),
        preparedBy: formDataOverride
          ? preparedBy || "Unknown"
          : entry.createdBy?.displayName || "Unknown",
        timeAndMaterialNumber: entry.timeAndMaterialNumber || "Draft",
        scopeOfWork: entry.scopeOfWork || "N/A",
        signedBy: entry.signedBy || "N/A",
        companyName: companyProfile?.companyName || "Your Company Name",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        companyLogo: companyLogoBase64 || null,
        signature: signatureBase64 || null,
        items,
        images: resolvedImages,
        subtotalPrice: subtotalPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        taxRate: taxRate.toFixed(2),
        taxAmount: taxAmount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        totalPrice: totalPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        overheadPercentage: entry.overheadPercentage
          ? entry.overheadPercentage.toFixed(2)
          : "0.00",
      };

      doc.setData(data);
      try {
        doc.render();
      } catch (renderError) {
        console.error("Error rendering Docxtemplater:", renderError);
        throw new Error("Failed to render document template.");
      }

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formDataPDF = new FormData();
      formDataPDF.append(
        "docx",
        docxBlob,
        `TandM_${entry._id || "draft"}.docx`
      );
      formDataPDF.append("documentType", "TandM");
      formDataPDF.append("identifier", entry._id || "draft");

      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formDataPDF,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        }
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        saveAs(pdfBlob, `TandM_${entry._id || "draft"}.pdf`);
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "T&M PDF generated successfully!",
        });
      } else {
        throw new Error("Failed to convert DOCX to PDF");
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate PDF: " + err.message,
      });
      console.error("Error in generateTandMPDF:", err);
    }
  };

  const handleStatusChange = async (entryId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const entry = timeAndMaterials.find((item) => item._id === entryId);

      if (newStatus === "Rejected" && entry.status === "Approved") {
        setRejectConfirm({ open: true, entryId });
        return;
      }

      const response = await axios.patch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${entryId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTimeAndMaterials((prev) =>
        prev.map((item) =>
          item._id === entryId ? response.data.timeAndMaterial : item
        )
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: `Status updated to ${newStatus}.`,
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to update status to ${newStatus}.`,
      });
      console.error("Error:", err);
    }
  };

  const confirmReject = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${rejectConfirm.entryId}/status`,
        { status: "Rejected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTimeAndMaterials((prev) =>
        prev.map((item) =>
          item._id === rejectConfirm.entryId
            ? response.data.timeAndMaterial
            : item
        )
      );
      setRejectConfirm({ open: false, entryId: null });
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "T&M entry rejected successfully.",
      });
    } catch (err) {
      setRejectConfirm({ open: false, entryId: null });
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to reject T&M entry.",
      });
      console.error("Error:", err);
    }
  };

  const handleConvertToChangeOrder = async (entry) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${entry._id}/convert-to-change-order`,
        { title: `Change Order from T&M #${entry.timeAndMaterialNumber}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const changeOrderId =
        response.data.changeOrders[response.data.changeOrders.length - 1]._id;
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Converted to Change Order successfully!",
        onConfirm: () => {
          setAlert({ open: false, type: "error", title: "", message: "" });
          navigate(
            `/projects/${project._id}/change-orders/${changeOrderId}/edit`
          );
        },
      });
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to convert to Change Order.",
      });
      console.error("Error:", err);
    }
  };

  const filteredTimeAndMaterials = timeAndMaterials.filter(
    (entry) =>
      (activeTab === "Pending" && entry.status === "Pending") ||
      (activeTab === "Approved" &&
        (entry.status === "Approved" || entry.status === "Rejected"))
  );

  const labourSubtotalCost = formData.labourItems.reduce(
    (sum, item) => sum + (item.budget ? item.totalCost : 0),
    0
  );
  const labourSubtotalPrice = formData.labourItems.reduce(
    (sum, item) => sum + (item.customer ? item.totalPrice : 0),
    0
  );
  const materialSubtotalCost = formData.materialItems.reduce(
    (sum, item) => sum + (item.budget ? item.totalCost : 0),
    0
  );
  const materialSubtotalPrice = formData.materialItems.reduce(
    (sum, item) => sum + (item.customer ? item.totalPrice : 0),
    0
  );

  const subtotalCost = labourSubtotalCost + materialSubtotalCost;
  const subtotalPrice = labourSubtotalPrice + materialSubtotalPrice;
  const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
  const taxAmount = subtotalPrice * (taxRate / 100);
  const totalPrice = subtotalPrice + taxAmount;

  if (loading) {
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  }

  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600">
      <div className="mx-auto px-4 py-6">
        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          confirmText="OK"
          onClose={() =>
            setAlert({ open: false, type: "error", title: "", message: "" })
          }
          onConfirm={
            alert.onConfirm ||
            (() =>
              setAlert({ open: false, type: "error", title: "", message: "" }))
          }
        />
        <AlertDialog
          open={rejectConfirm.open}
          type="warning"
          title="Confirm Rejection"
          message="Rejecting this approved T&M will reverse the entry and remove associated items from purchase orders and budgets. This action cannot be undone. Are you sure you want to proceed?"
          confirmText="Reject"
          cancelText="Cancel"
          onClose={() => setRejectConfirm({ open: false, entryId: null })}
          onConfirm={confirmReject}
        />
        {viewMode === "view" && selectedEntry ? (
          <TandMView
            selectedEntry={selectedEntry}
            companyProfile={companyProfile}
            availableLabourItems={availableLabourItems}
            availableMaterialItems={availableMaterialItems}
            costCodeOptions={costCodeOptions}
            vendorOptions={vendorOptions}
            bidAreaOptions={bidAreaOptions}
            setViewMode={setViewMode}
            generateTandMPDF={generateTandMPDF}
            project={project}
            setTimeAndMaterials={setTimeAndMaterials}
            setProject={setProject}
            formData={formData}
            setFormData={setFormData}
            selectedLabourItem={selectedLabourItem}
            setSelectedLabourItem={setSelectedLabourItem}
            selectedMaterialItem={selectedMaterialItem}
            setSelectedMaterialItem={setSelectedMaterialItem}
            labourQty={labourQty}
            setLabourQty={setLabourQty}
            materialQty={materialQty}
            setMaterialQty={setMaterialQty}
            images={images}
            setImages={setImages}
            selectedVendor={selectedVendor}
            setSelectedVendor={setSelectedVendor}
            selectedLabourBidArea={selectedLabourBidArea}
            setSelectedLabourBidArea={setSelectedLabourBidArea}
            selectedMaterialAnticipatedBidArea={
              selectedMaterialAnticipatedBidArea
            }
            setSelectedMaterialAnticipatedBidArea={
              setSelectedMaterialAnticipatedBidArea
            }
            selectedLabourCostCode={selectedLabourCostCode}
            setSelectedLabourCostCode={setSelectedLabourCostCode}
            selectedMaterialCostCode={selectedMaterialCostCode}
            setSelectedMaterialCostCode={setSelectedMaterialCostCode}
            editingLabourItemIndex={editingLabourItemIndex}
            setEditingLabourItemIndex={setEditingLabourItemIndex}
            editingMaterialItemIndex={editingMaterialItemIndex}
            setEditingMaterialItemIndex={setEditingMaterialItemIndex}
            editLabourForm={editLabourForm}
            setEditLabourForm={setEditLabourForm}
            editMaterialForm={editMaterialForm}
            setEditMaterialForm={setEditMaterialForm}
            setIsCreating={setIsCreating}
            setParentIsEditing={setIsEditing}
            setEditingId={setEditingId}
          />
        ) : isCreating ? (
          <TandMForm
            isEditing={isEditing}
            editingId={editingId}
            formData={formData}
            generateTandMPDF={generateTandMPDF}
            setFormData={setFormData}
            availableLabourItems={availableLabourItems}
            availableMaterialItems={availableMaterialItems}
            selectedLabourItem={selectedLabourItem}
            setSelectedLabourItem={setSelectedLabourItem}
            selectedMaterialItem={selectedMaterialItem}
            setSelectedMaterialItem={setSelectedMaterialItem}
            labourQty={labourQty}
            handleDeleteLabourItem={handleDeleteLabourItem}
            setLabourQty={setLabourQty}
            materialQty={materialQty}
            setMaterialQty={setMaterialQty}
            images={images}
            setImages={setImages}
            vendors={vendors}
            costCodes={costCodes}
            selectedVendor={selectedVendor}
            setSelectedVendor={setSelectedVendor}
            selectedLabourBidArea={selectedLabourBidArea}
            setSelectedLabourBidArea={setSelectedLabourBidArea}
            selectedMaterialAnticipatedBidArea={
              selectedMaterialAnticipatedBidArea
            }
            setSelectedMaterialAnticipatedBidArea={
              setSelectedMaterialAnticipatedBidArea
            }
            selectedLabourCostCode={selectedLabourCostCode}
            setSelectedLabourCostCode={setSelectedLabourCostCode}
            selectedMaterialCostCode={selectedMaterialCostCode}
            setSelectedMaterialCostCode={setSelectedMaterialCostCode}
            bidAreaOptions={bidAreaOptions}
            editingLabourItemIndex={editingLabourItemIndex}
            setEditingLabourItemIndex={setEditingLabourItemIndex}
            editingMaterialItemIndex={editingMaterialItemIndex}
            setEditingMaterialItemIndex={setEditingMaterialItemIndex}
            editLabourForm={editLabourForm}
            setEditLabourForm={setEditLabourForm}
            editMaterialForm={editMaterialForm}
            setEditMaterialForm={setEditMaterialForm}
            handleCreateToggle={handleCreateToggle}
            companyProfile={companyProfile}
            preparedBy={preparedBy}
            setAlert={setAlert}
            vendorOptions={vendorOptions}
            costCodeOptions={costCodeOptions}
            subtotalCost={subtotalCost}
            subtotalPrice={subtotalPrice}
            taxRate={taxRate}
            taxAmount={taxAmount}
            totalPrice={totalPrice}
            project={project}
            setTimeAndMaterials={setTimeAndMaterials}
            setProject={setProject}
            setIsCreating={setIsCreating}
            setIsEditing={setIsEditing}
            setEditingId={setEditingId}
            setViewMode={setViewMode}
          />
        ) : (
          <TandMList
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            filteredTimeAndMaterials={filteredTimeAndMaterials}
            handleCreateToggle={handleCreateToggle}
            handleEdit={handleEdit}
            handleView={handleView}
            handleStatusChange={handleStatusChange}
            handleConvertToChangeOrder={handleConvertToChangeOrder}
            costCodeOptions={costCodeOptions}
            availableLabourItems={availableLabourItems}
            vendorOptions={vendorOptions}
          />
        )}
      </div>
    </div>
  );
}
